import { useEffect, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { KeyRound, Mail } from "lucide-react";

import {
  previewPartnerToken,
  redeemPartnerToken,
  requestPartnerLogin,
  type PartnerLoginPreview,
} from "@/lib/partner-api";

type LoginSearch = { token?: string; redirect?: string };

export const Route = createFileRoute("/$tenantSlug/partner/login")({
  component: PartnerLoginRedeem,
  validateSearch: (search): LoginSearch => ({
    token: typeof search.token === "string" && search.token ? search.token : undefined,
    redirect:
      typeof search.redirect === "string" && search.redirect ? search.redirect : undefined,
  }),
});

function PartnerLoginRedeem() {
  const { tenantSlug } = Route.useParams();
  const { token, redirect } = Route.useSearch();
  const navigate = useNavigate();
  const [preview, setPreview] = useState<PartnerLoginPreview | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) {
      void navigate({ to: "/$tenantSlug/partner", params: { tenantSlug } });
      return;
    }
    let cancelled = false;
    previewPartnerToken(tenantSlug, token).then(
      (p) => {
        if (cancelled) return;
        setPreview(p);
        if (p.state === "session_already_active") {
          void navigateToDestination(navigate, tenantSlug, redirect);
        }
      },
      (err: Error) => {
        if (cancelled) return;
        setPreviewError(err.message);
      },
    );
    return () => {
      cancelled = true;
    };
  }, [tenantSlug, token, redirect, navigate]);

  if (!token) return null;
  if (previewError) {
    return (
      <ResendForm
        tenantSlug={tenantSlug}
        intro="Something went wrong loading this sign-in link."
      />
    );
  }
  if (!preview) {
    return (
      <div className="flex-1 grid place-items-center p-8 text-sm text-slate-500">
        Loading&hellip;
      </div>
    );
  }
  if (preview.state === "session_already_active") {
    return (
      <div className="flex-1 grid place-items-center p-8 text-sm text-slate-500">
        Taking you back to the partner portal&hellip;
      </div>
    );
  }
  if (preview.state === "valid") {
    return (
      <SignInInterstitial
        tenantSlug={tenantSlug}
        token={token}
        redirect={redirect}
        partnerName={preview.partner_name}
        company={preview.company}
      />
    );
  }
  return <ResendForm tenantSlug={tenantSlug} intro={introForFailedState(preview.state)} />;
}

function introForFailedState(state: "expired" | "not_found"): string {
  if (state === "expired") {
    return "This sign-in link has expired. Request a fresh one below.";
  }
  return "This sign-in link is not valid. Request a fresh one below.";
}

function SignInInterstitial({
  tenantSlug,
  token,
  redirect,
  partnerName,
  company,
}: {
  tenantSlug: string;
  token: string;
  redirect: string | undefined;
  partnerName: string | null;
  company: string | null;
}) {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onClick = () => {
    setSubmitting(true);
    setError(null);
    redeemPartnerToken(tenantSlug, token).then(
      () => {
        void navigateToDestination(navigate, tenantSlug, redirect);
      },
      (err: Error) => {
        setError(err.message);
        setSubmitting(false);
      },
    );
  };

  const greeting = partnerName
    ? company
      ? `Signing in as ${partnerName} at ${company}.`
      : `Signing in as ${partnerName}.`
    : "";

  return (
    <div className="flex-1 grid place-items-center p-6 sm:p-10 bg-slate-50 dark:bg-slate-950">
      <div className="w-full max-w-md bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-6 sm:p-8 space-y-4">
        <h1 className="text-lg font-medium inline-flex items-center gap-2">
          <KeyRound className="size-4" /> Sign in to the partner portal
        </h1>
        <p className="text-sm text-slate-500">
          {greeting} Click below to continue.
        </p>
        <button
          type="button"
          onClick={onClick}
          disabled={submitting}
          className="w-full rounded-md bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900 px-4 py-2 text-sm font-medium disabled:opacity-60"
        >
          {submitting ? "Signing in…" : "Sign in"}
        </button>
        {error ? <p className="text-xs text-red-600 dark:text-red-400">{error}</p> : null}
      </div>
    </div>
  );
}

function ResendForm({ tenantSlug, intro }: { tenantSlug: string; intro: string }) {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    requestPartnerLogin(tenantSlug, email.trim()).then(
      () => {
        setSent(true);
        setSubmitting(false);
      },
      (err: Error) => {
        setError(err.message);
        setSubmitting(false);
      },
    );
  };

  return (
    <div className="flex-1 grid place-items-center p-6 sm:p-10 bg-slate-50 dark:bg-slate-950">
      <div className="w-full max-w-md bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-6 sm:p-8 space-y-4">
        <h1 className="text-lg font-medium inline-flex items-center gap-2">
          <KeyRound className="size-4" /> Sign in to the partner portal
        </h1>
        <p className="text-sm text-slate-500">{intro}</p>
        {sent ? (
          <div className="rounded-md border border-emerald-200 bg-emerald-50 dark:bg-emerald-900/20 dark:border-emerald-700 p-3 space-y-1">
            <div className="inline-flex items-center gap-2 text-sm font-medium text-emerald-700 dark:text-emerald-300">
              <Mail className="size-4" /> Check your inbox
            </div>
            <p className="text-xs text-emerald-700/80 dark:text-emerald-300/80">
              If your email is registered, you&apos;ll receive a fresh sign-in link within a few minutes.
            </p>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3">
            <div className="space-y-1.5">
              <label htmlFor="partner-resend-email" className="text-sm font-medium">
                Email
              </label>
              <input
                id="partner-resend-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="block w-full rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-950 px-3 py-2 text-sm"
                autoFocus
              />
            </div>
            <button
              type="submit"
              disabled={submitting || !email.includes("@")}
              className="w-full rounded-md bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900 px-4 py-2 text-sm font-medium disabled:opacity-60"
            >
              {submitting ? "Sending…" : "Send me a new sign-in link"}
            </button>
            {error ? <p className="text-xs text-red-600 dark:text-red-400">{error}</p> : null}
          </form>
        )}
      </div>
    </div>
  );
}

function navigateToDestination(
  navigate: ReturnType<typeof useNavigate>,
  tenantSlug: string,
  redirect: string | undefined,
): Promise<void> {
  if (redirect && redirect.startsWith("/") && !redirect.startsWith("//")) {
    window.location.assign(redirect);
    return Promise.resolve();
  }
  return navigate({ to: "/$tenantSlug/partner", params: { tenantSlug } });
}
